/**
 * @file http请求控制
 * @author mengchen <mengchen@babytree-inc.com>
 */
import axios from 'axios';
import queryString from './query-string';
import {
  getGOHost,
  getNewMapHost,
  getPHPHost,
  getMapiWebHost,
  getABTestingHost,
  getOpenHost,
  getWebviewHost,
  getMeitunHost
} from './config';
import { Loading } from './ui';
import { Toast } from 'antd-mobile';
import md5 from 'md5';
import { getToken } from './userinfo';
import { isInMiniProgram, isInFosun } from '@utils/development';
import { logout } from './logout';
import Ready, { BBT_WEBVIEW_H5_READY } from './app-ready';
import { bbtNative } from '@bbtfe/webview-bridge';
import { sendCustomEvent } from '@utils/volcengine';
import { getPlatform } from '@utils/constants';
import { getDeviceId } from '@utils/deviceid';
import { InterfaceError } from './error';

const requestLoading = {
  isLoading: false,
  cacheList: [],
  start(cache, message) {
    if (this.cacheList.length == 0 && this.isLoading == false) {
      this.isLoading = true;
      Loading.open({
        message
      });
    }
    this.cacheList.push(cache);
  },
  end(cache) {
    const index = this.cacheList.indexOf(cache);
    if (index > -1) {
      this.cacheList.splice(index, 1);
    }
    if (this.cacheList.length == 0 && this.isLoading == true) {
      Loading.close();
      this.isLoading = false;
    }
  }
};

const instance = axios.create({
  withCredentials: true,
  timeout: 5000,
  headers: {
    Accept: '*/*'
  }
});

const requestLocker = {};

const defaultOption = {
  toast: true,
  service: 'go',
  loading: true,
  loadingMessage: '',
  unkonwErrorRetry: 1
};

export const getBaseURL = function (service) {
  if (service.toLocaleLowerCase() == 'go') {
    return getGOHost();
  }
  if (service.toLocaleLowerCase() == 'php') {
    return getPHPHost();
  }
  if (service.toLocaleLowerCase() == 'newmapi') {
    return getNewMapHost();
  }
  if (service.toLocaleLowerCase() == 'mapiweb') {
    return getMapiWebHost();
  }
  if (service.toLocaleLowerCase() == 'abtesting') {
    return getABTestingHost();
  }
  if (service.toLocaleLowerCase() == 'open') {
    return getOpenHost();
  }
  if (service.toLocaleLowerCase() == 'webview') {
    return getWebviewHost();
  }
  if (service.toLocaleLowerCase() == 'meitun') {
    return getMeitunHost();
  }
  return '';
};

const getBaseParams = service => {
  if (service.toLocaleLowerCase() == 'meitun') {
    let constraintsPlatform = 1; // App
    if (isInMiniProgram()) {
      constraintsPlatform = 2;
    } else if (isInFosun()) {
      constraintsPlatform = 3;
    }
    return {
      constraintsPlatform
    };
  }
  return {};
};

// secretKey 的值请咨询公共支付团队
const secretKey = 'iambabytreekey!@#$%^&*()';

/**
 * 生成签名的方法
 * @description 进行签名运算的串为：key1=value1key2=value2${secretKey}
 */
const signuature = obj => {
  let str = '';
  Object.keys(obj)
    .sort((a, b) => a.toLowerCase().localeCompare(b))
    .forEach(key => {
      str += `${key.toLowerCase()}=${obj[key]}`;
    });
  str += secretKey;
  return md5(str);
};

const requestfactory = async (...params) => {
  if (bbtNative.isApp() && !BBT_WEBVIEW_H5_READY) {
    await Ready();
  }
  return await requestfactoryFunc(...params).catch(e => {
    throw e;
  });
};

const requestfactoryFunc = function (url, axiosOptions = {}, options = {}) {
  if (options.key && requestLocker[`${options.key}`]) {
    const error = new Error(`${options.key} key is working`);
    return Promise.reject(error);
  }
  options = {
    ...defaultOption,
    ...options
  };
  options.key && (requestLocker[`${options.key}`] = true);
  const cache = +new Date();
  options.loading && requestLoading.start(cache, options.loadingMessage);
  axiosOptions.baseURL = getBaseURL(options.service);
  axiosOptions.url = url || axiosOptions.url;
  axiosOptions.params = axiosOptions.params || {};
  // 动态更改请求超时时间
  instance.defaults.timeout =
    axiosOptions?.timeout || instance?.defaults.timeout;
  // 请求头加token
  if (getToken()) {
    axiosOptions.headers.loginstring = getToken();
  }
  axiosOptions.headers.signature = signuature({
    ...axiosOptions?.allParams,
    ...{ timestamp: axiosOptions?.headers?.timestamp }
  });
  // axiosOptions.params.cache = cache;1

  return instance
    .request(axiosOptions)
    .then((response = {}) => {
      options.key && (requestLocker[`${options.key}`] = false);
      options.loading && requestLoading.end(cache);
      const { status, data = {} } = response;
      if ((status == 200 && data.status == 'success') || data.rtn_code == '0') {
        return data;
      }
      if (
        (status == 200 && data.status == 'success') ||
        data?.rescode?.code == '0'
      ) {
        return data;
      }
      /** 不校验数据针对老接口 */
      if (options?.freeCheck && status == 200) {
        return data;
      }
      /** 登录失败/过期 */
      if (!options?.freeLogin && data?.rtn_code == '0000002') {
        sendCustomEvent({
          name: '用户登录信息失效',
          categories: {
            detail: getToken()
          }
        });
        logout();
      }
      const error = new InterfaceError(
        data?.message || data?.rtn_msg || data?.msg || '系统繁忙请稍后重试'
      );
      error.status = data.status || data.rtn_code;
      error.data = data.data;
      error.response = response;
      error.isApiError = true;
      throw error;
    })
    .catch(error => {
      options.key && delete requestLocker[`${options.key}`];
      options.loading && requestLoading.end(cache);
      const message = error?.message?.toLowerCase();
      if (message?.indexOf('network error') > -1) {
        const { unkonwErrorRetry } = options;
        if (unkonwErrorRetry > 0) {
          return requestfactory(url, axiosOptions, {
            ...options,
            unkonwErrorRetry: unkonwErrorRetry - 1
          });
        }
      }
      if (message?.indexOf('timeout') > -1) {
        error.isTimeout = true;
        error.message = '请求超时，请稍后重试';
        error.url = url;
      }
      options.toast && Toast.show(error?.message, 2, null, false);
      throw error;
    });
};

const handleParams = allParams => {
  const cloneAllParams = { ...allParams };
  for (const key in allParams) {
    const value = allParams[key];
    if (value === undefined || value === null || value === '') {
      delete cloneAllParams[key];
    }
  }
  return cloneAllParams;
};

export const request = {
  get({ url, data = {}, axiosOptions = {}, options = {} }) {
    const allParams = handleParams({
      ...getBaseParams(options.service),
      ...data
    });
    return requestfactory(
      url,
      {
        ...axiosOptions,
        method: 'get',
        params: allParams,
        allParams
      },
      options
    );
  },
  post({ url, data = {}, axiosOptions = {}, options = {} }) {
    axiosOptions.headers = axiosOptions.headers || {};
    axiosOptions.headers['Content-Type'] = 'application/x-www-form-urlencoded';
    const allParams = handleParams({
      ...getBaseParams(options.service),
      ...data
    });
    return requestfactory(
      url,
      {
        ...axiosOptions,
        method: 'post',
        data: queryString.stringify(allParams),
        allParams
      },
      options
    );
  },
  postJson({
    url,
    data = {},
    axiosOptions = {},
    options = {},
    method = 'post'
  }) {
    axiosOptions.headers = axiosOptions.headers || {};
    axiosOptions.headers['Content-Type'] = 'application/json';
    return requestfactory(
      url,
      {
        ...axiosOptions,
        method,
        data
      },
      options
    );
  },
  postForm({ url, data = {}, axiosOptions = {}, options = {} }) {
    axiosOptions.headers = axiosOptions.headers || {};
    axiosOptions.headers['Content-Type'] = 'form-data';
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      formData.append(key, data[key]);
    });
    return requestfactory(
      url,
      {
        ...axiosOptions,
        method: 'post',
        data: formData,
      },
      options
    );
  },
  request({ url, data, axiosOptions = {}, options = {} }) {
    const method = axiosOptions.method || 'get';
    if (method == 'get') {
      axiosOptions.params = data;
    } else {
      axiosOptions.data = data;
    }
    return requestfactory(
      url,
      {
        ...axiosOptions,
        method
      },
      options
    );
  }
};

export const getMapiHeaders = function (data) {
  const queryObject = queryString.parseUrl(window.location.search);
  const { query } = queryObject;
  return {
    birthday: query['bpreg-brithday'] || '1900-01-01',
    platform: getPlatform(),
    clientInfo: JSON.stringify({
      clientSystem: /Android/.test(window.navigator.userAgent)
        ? 'Android'
        : 'IOS',
      clientAppVersion: '2.8.0',
      clientYunyuVersion: query['babytree-app-version'] || '9.20.0',
      clientVersion: query['babytree-app-version'],
      latitude: query['babytree-app-latitude'],
      longitude: query['babytree-app-longitude'],
      lbu: query.lbu || getDeviceId(),
      deviceCode: query['android-id'] || query['babytree-app-mac'],
      screenwidth: window.screen.width,
      screenheight: window.screen.height,
      partner: 'babytree'
    }),
    protocol: document.location.protocol == 'https:' ? 'https' : 'http',
    signature: 'ddd',
    timestamp: Math.floor(+new Date() / 1000),
    ...data
  };
};
